/**
 * Hook to register global FE tools for the in-app agent.
 *
 * This hook should be called once inside OsLayout. It registers fe_show / fe_close
 * (ref-based navigation, see services/agent/aniRef.ts) and fe_open_ask_ani.
 */

import { useEffect, useRef } from "react";
import { isFeOpenAppIdAllowed } from "@/services/agent/agentNavigation";
import {
  registerGlobalTools,
  feToolRegistry,
  setFeOpenAppAllowlist,
  getFeOpenAppAllowlist,
  OPENWORK_DEMO_OPEN_APP_IDS,
  type FeToolResult,
} from "@/services/agent";
import {
  narrowAgentWindowParams,
  type AgentWindowParams,
} from "@/components/ui/os-layout/osAgentWindowParams";
import {
  openArtifactById,
  type OpenArtifactSurface,
} from "@/components/artifacts/openArtifactDestination";
import type { OsAniSnapshotSource } from "@/components/ui/os-layout/buildOsAniContextSnapshot";
import { parseUrlToWindowConfig } from "@/components/ui/os-layout/urlMapping";
import { APP_REGISTRY, canAccessWindow } from "@/components/ui/os-layout/OsAppRegistry";
import { useAuth } from "@/context/AuthContext";
import { useModulePermissions } from "@/context/ModulePermissionsContext";

interface GlobalFeToolsOptions {
  openWindow: (config: {
    appId: string;
    title?: string;
    params?: AgentWindowParams;
  }) => void;
  closeWindow: (id: string) => boolean;
  focusWindow: (id: string) => void;
  updateWindow: (id: string, patch: { isMaximized?: boolean; isMinimized?: boolean }) => void;
  openSearch: (query: string) => void;
  readSnapshot: () => OsAniSnapshotSource;
}

/**
 * Registers global FE tools for the in-app AI agent.
 *
 * Call this once in a component that:
 * - Is inside OsProvider (has access to openWindow)
 * - Mounts early in the app lifecycle
 * - Stays mounted for the app's lifetime
 *
 * The hook is idempotent - calling it multiple times is safe.
 *
 * @param options - Handlers from OsContext for window management
 */
export function useGlobalFeTools(options: GlobalFeToolsOptions): void {
  const { user } = useAuth();
  const { hasPermission, permissions: modulePermissions } = useModulePermissions();
  const registeredRef = useRef(false);
  const optionsRef = useRef(options);
  const accessRef = useRef({ user, modulePermissions, hasPermission });
  accessRef.current = { user, modulePermissions, hasPermission };
  
  // Keep options ref updated
  optionsRef.current = options;

  useEffect(() => {
    // Only register once
    if (registeredRef.current) {
      return;
    }

    // Check if tools are already registered (e.g., from HMR)
    if (feToolRegistry.hasTool("fe_show")) {
      registeredRef.current = true;
      return;
    }

    const isOpenworkDemo = user?.company_id === "openworks";
    if (isOpenworkDemo) {
      setFeOpenAppAllowlist(OPENWORK_DEMO_OPEN_APP_IDS);
    }

    // App ID to title mapping
    const appTitles: Record<string, string> = {
      home: "Home",
      "task-review": "Review",
      artifacts: "Letters",
      artifact: "Letter",
      missions: "Missions",
      "mission-control": "Mission Control",
      "case-checking": "Case Checking",
      disclosures: "Disclosures",
      meetings: "Meetings",
      "cash-flow": "Cash Flow",
      screener: "Screener",
      portfolio: "Portfolio",
      "report-builder": "Report Builder",
      dashboard: "Dashboard",
      documents: "Documents",
      pods: "Pods",
      workers: "Workers",
    };

    const rejectInaccessibleApp = (appId: string): FeToolResult | undefined => {
      const { hasPermission, user, modulePermissions } = accessRef.current;
      if (!canAccessWindow(appId, hasPermission, user, modulePermissions)) {
        return { success: false, message: `App "${appId}" is not available to this user` };
      }
      if (!isFeOpenAppIdAllowed(appId, getFeOpenAppAllowlist())) {
        return { success: false, message: `"${appId}" is not available in this profile.` };
      }
      return undefined;
    };

    const openOsWindow = (appId: string, params?: Record<string, unknown>): FeToolResult | void => {
        const appDef = APP_REGISTRY[appId];
        if (!appDef) {
          return {
            success: false,
            message: `Unknown app "${appId}" — not in APP_REGISTRY`,
          };
        }
        const rejection = rejectInaccessibleApp(appId);
        if (rejection) {
          return rejection;
        }
        const title = appTitles[appId] || appId;
        optionsRef.current.openWindow({
          appId,
          title,
          params: narrowAgentWindowParams(params),
        });
    };

    registerGlobalTools({
      openWindow: openOsWindow,

      openArtifact: async (artifactId: string): Promise<OpenArtifactSurface | FeToolResult> => {
        let windowError: FeToolResult | undefined;
        const surface = await openArtifactById(
          artifactId,
          (config) => {
            const windowResult = openOsWindow(config.appId, config.params as Record<string, unknown>);
            if (windowResult && !windowResult.success) {
              windowError = windowResult;
            }
          },
          { mode: "view" },
        );
        return windowError ?? surface;
      },

      openTaskReview: (taskId: string): FeToolResult | void => {
        const config = parseUrlToWindowConfig(`/review/${taskId}`);
        if (!config) {
          return { success: false, message: `Could not open review for task ${taskId}` };
        }
        const rejection = rejectInaccessibleApp(config.appId);
        if (rejection) {
          return rejection;
        }
        optionsRef.current.openWindow({
          appId: config.appId,
          title: "Review",
          params: narrowAgentWindowParams(config.params as Record<string, unknown>),
        });
      },

      closeWindow: (windowId: string) => optionsRef.current.closeWindow(windowId),
      focusWindow: (windowId: string) => optionsRef.current.focusWindow(windowId),
      updateWindow: (windowId, patch) => optionsRef.current.updateWindow(windowId, patch),
      openSearch: (query: string) => optionsRef.current.openSearch(query),
      readSnapshot: () => optionsRef.current.readSnapshot(),
    });

    registeredRef.current = true;
  }, [user?.company_id]);
}
